import React, { useState } from 'react';
import {
    Box,
    Collapse,
    FormControl,
    FormControlProps,
    IconButton,
    InputLabel,
    LinearProgress,
    MenuItem,
    NativeSelect,
    Select,
    SelectChangeEvent
} from '@mui/material';
import { Clear } from '@mui/icons-material';
import useMyDropdowns from 'hooks/useMyDropdowns';

export interface TargetTenantDropdownProps {
    onChange: (value: string | string[]) => void;
    recordTypeId: number;
    initialValue?: string | string[];
    variant?: FormControlProps['variant'];
    size?: FormControlProps['size'];
    label?: string;
    native?: boolean;
    multiple?: boolean;
    fromFilterPanel?: boolean;
}

export const TargetTenantDropdown = ({
    recordTypeId,
    onChange,
    initialValue,
    native,
    label,
    variant = 'outlined',
    size = 'medium',
    multiple = false
}: TargetTenantDropdownProps) => {
    const [selectedTenant, setSelectedTenant] = useState<string>('');
    const [selectedMultipleTenant, setSelectedMultipleTenant] = useState<string[]>([]);

    const { loading, targetTenantList } = useMyDropdowns({
        recordTypeId,
        dropdowns: ['TARGET_TENANTS'],
        onCompleted(data) {
            const list = data.recordStatusList;
            if (list && initialValue) {
                if (multiple) {
                    setSelectedMultipleTenant((initialValue as string[]) || []);
                } else {
                    setSelectedTenant(String(initialValue) || '');
                }
            }
        }
    });

    const handleValueChange = (event: SelectChangeEvent<string | string[]> | React.ChangeEvent<{ value: string }>) => {
        const newValue = event.target.value;
        if (multiple) setSelectedMultipleTenant(newValue as string[]);
        else setSelectedTenant(newValue as string);
        onChange(newValue);
    };

    const handleClearClick = () => {
        const newValue = multiple ? ['0'] : '0';
        if (multiple) setSelectedMultipleTenant(newValue as string[]);
        else setSelectedTenant(newValue as string);
        onChange(newValue);
    };

    return (
        <Box sx={{ minWidth: 149, width: '100%' }}>
            <FormControl fullWidth disabled={loading}>
                {label && (
                    <InputLabel id="record-status-select" shrink>
                        {label}
                    </InputLabel>
                )}
                {native ? (
                    <NativeSelect value={selectedTenant} onChange={handleValueChange}>
                        <option value=""> &nbsp;</option>
                        {targetTenantList?.map((el) => (
                            <option key={el.targetTenantToTransmit.id} value={el.targetTenantToTransmit.id}>
                                {el.targetTenantToTransmit.name}
                            </option>
                        ))}
                    </NativeSelect>
                ) : (
                    <Select
                        value={multiple ? selectedMultipleTenant : selectedTenant}
                        onChange={handleValueChange}
                        multiple={multiple}
                        size={size}
                        variant={variant}
                        sx={{ pt: '3px !important', '& .MuiOutlinedInput-notchedOutline': { border: 'none' } }}
                        endAdornment={
                            <IconButton
                                sx={{
                                    display: !multiple && selectedTenant && selectedTenant !== '0' ? '' : 'none'
                                }}
                                onClick={handleClearClick}
                            >
                                <Clear />
                            </IconButton>
                        }
                        {...(!multiple && selectedTenant && selectedTenant !== '0' && { IconComponent: () => null })}
                    >
                        {targetTenantList?.map((el) => (
                            <MenuItem key={el.targetTenantToTransmit.id} value={el.targetTenantToTransmit.id}>
                                {el.targetTenantToTransmit.name}
                            </MenuItem>
                        ))}
                    </Select>
                )}
                <Collapse in={loading}>
                    <LinearProgress />
                </Collapse>
            </FormControl>
        </Box>
    );
};
